"use client";

import useSWR from "swr";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { CalendarBlank, CaretDown, MapPinSimpleArea } from "@phosphor-icons/react";
import { useForm } from "react-hook-form";
import { tourDefaultValue, tourSchema } from "@/shared/schemas/tour/tourTab.schema";
import type { TourSchema } from "@/shared/schemas/tour/tourTab.schema";
import fetcher, { type ExtendedResponse } from "@/shared/libs/fetcher";
import apis from "@/configs/apis";
import useProgress from "@/shared/hooks/useProgress";
import { jalaliToGregorian } from "@/shared/libs/date";
import { createRoomsPattern } from "@/shared/libs/utils";
import LocationPicker from "../../locationPicker";
import { DrawerClose } from "@/shared/components/common/drawer/drawer";
import { PopoverClose } from "@/shared/components/common/popover";
import RoomPicker from "../../roomPicker/RoomPicker";
import { yupResolver } from "@hookform/resolvers/yup";
import Datepicker from "@/shared/components/common/datepicker/Datepicker";
import TabContent from "../TabContent";
import { isNumber } from "@/shared/libs/type-utils";
import { useDeviceType } from "@/shared/context/DeviceContext";
type TFormField = "date" | "origin" | "destination" | "rooms";
interface TourTabProps {
  page?: "landing" | "result";
  searchParams?: SearchParams;
}
export default function TourTab({
  page = "landing",
  searchParams
}: Readonly<TourTabProps>) {
  const userDeviceType = useDeviceType();
  const router = useRouter();
  const {
    checkIn,
    checkOut,
    rooms
  } = searchParams ?? {};
  const [search, setSearch] = useState("");
  const {
    data: citiesResponse,
    isLoading
  } = useSWR<ExtendedResponse<TourCitiesRes>>(`${apis.tour.cities.client}`, fetcher, {
    revalidateOnMount: true,
    revalidateIfStale: false,
    revalidateOnFocus: true,
    revalidateOnReconnect: false
  });
  const {
    data: cities
  } = citiesResponse ?? {};
  const findCity = search.length > 0 ? cities?.results?.filter(city => [city.fa_name, city.name].some(field => field.toLowerCase().includes(search.toLowerCase()))) : [];
  const form = useForm<TourSchema>({
    resolver: yupResolver(tourSchema),
    defaultValues: {
      date: checkIn ? {
        depart: checkIn ? checkIn : "",
        return: checkOut ? checkOut : ""
      } : undefined,
      rooms: rooms ? createRoomsPattern("decode", rooms) as {
        adult: number;
        child: string[];
      }[] : tourDefaultValue.rooms,
      origin: undefined,
      destination: undefined
    }
  });
  const {
    watch,
    setValue,
    getValues
  } = form;
  const date = watch("date");
  const progressInputs = [{
    id: 0,
    name: "origin",
    value: watch("origin.id")
  }, {
    id: 1,
    name: "destination",
    value: watch("destination.id")
  }, {
    id: 2,
    name: "date",
    value: watch("date.depart")
  }, {
    id: 3,
    name: "rooms",
    value: watch("rooms").filter(room => room.adult > 0).length > 0 ? watch("rooms").filter(room => room.adult > 0).length : undefined
  }];
  const {
    handleProgress,
    openStatus
  } = useProgress(progressInputs);
  const handleSetValues = ({
    paramName,
    id,
    func
  }: {
    paramName: TFormField;
    id: number;
    func?: (value: any) => any;
  }) => (value: any) => {
    if (func) {
      setValue(paramName, func(value));
    } else {
      setValue(paramName, value);
    }
    id !== 4 && handleProgress(id, true);
    search && setSearch("");
  };
  const icons: {
    [key: string]: ReactNode;
  } = {
    origin: <MapPinSimpleArea className="w-5 h-5" />,
    destination: <MapPinSimpleArea className="w-5 h-5" />,
    date: <CalendarBlank className="w-5 h-5" />,
    rooms: <CaretDown className="w-5 h-5" />
  };
  const inputs = (key: string, paramName: TFormField, id: number, func?: (value: any) => string) => {
    const searchResult = {
      data: findCity,
      isLoading,
      search,
      setSearch: (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)
    };
    const inputObject = {
      origin: <LocationPicker route="origin" formName="tour"
      // @ts-ignore
      searchResult={searchResult} onClick={handleSetValues({
        paramName,
        id,
        func
      })} OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose} />,
      destination: <LocationPicker route="destination" formName="tour"
      // @ts-ignore
      searchResult={searchResult} onClick={handleSetValues({
        paramName,
        id,
        func
      })} OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose} />,
      date: <Datepicker onPickDate={handleSetValues({
        paramName,
        id,
        func
      })} OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose} mode="range" selectedDate={{
        from: date?.depart ? new Date(jalaliToGregorian(date.depart)) : undefined,
        to: date?.return ? new Date(jalaliToGregorian(date.return)) : undefined
      }} />,
      rooms: <RoomPicker roomData={getValues("rooms")} OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose} service="tour" onClick={handleSetValues({
        paramName,
        id,
        func
      })} />
    };
    return inputObject[key as TFormField];
  };
  const onSubmit = (data: TourSchema) => {
    const {
      date,
      destination,
      origin,
      rooms
    } = data;
    localStorage.setItem("drawerOpen", JSON.stringify(false));
    router.push(`/tour/${origin.code}-${destination.code}/?checkIn=${date.depart?.replaceAll("/", "-") ?? ""}&checkOut=${date.return?.replaceAll("/", "-") ?? ""}&rooms=${createRoomsPattern("encode", rooms)}${`&origin=${origin.id}`}${`&destination=${destination.id}`}`);
  };

  // Search for all cities

  // set input values using searchParams
  useEffect(() => {
    const resutls = cities?.results;
    if (cities?.succeed && resutls && searchParams) {
      const origin = resutls.find(city => city.id === +searchParams.origin);
      const destination = resutls.find(city => city.id === +searchParams.destination);
      if (page === "result" && origin && destination) {
        setValue("origin", {
          name: origin.fa_name,
          code: origin.name,
          type: "city",
          id: origin.id
        });
        setValue("destination", {
          name: destination?.fa_name,
          code: destination?.name,
          type: "city",
          id: destination.id
        });
      }
    }
  }, [cities, page, searchParams, setValue]);
  return <TabContent<TFormField, TourSchema> page={page} services="tour" className={(id, type) => `${isNumber(id) && id === 2 ? "col-span-12 sm:col-span-6 lg:col-span-3" : id === 3 ? "col-span-12 sm:col-span-6 lg:col-span-3" : "col-span-12 lg:col-span-2"} ${type !== "origin" && type !== "destination" && "md:px-1"} ${type === "destination" && "md:pl-2"} relative`} hasLocationSwitch inputConfig={{
    icons: icons,
    inputs,
    progressInputs: {
      handleProgress,
      openStatus
    }
  }}
  // @ts-ignore
  form={form}
  // @ts-ignore
  onSubmit={onSubmit} data-sentry-element="TabContent" data-sentry-component="TourTab" data-sentry-source-file="TourTab.tsx" />;
}